/* Soft — the shared control atoms (operator's pick 2026-10-08; review stand
   /test/style-v3). A ported surface uses these instead of redrawing them, so
   every page presses, focuses and rests the same way:

     .btn     raised — the rim tone, flat: 36 high, radius 999
     .round   a square .btn (36×36); with .small, 32×32
     .field   recessed — a 3px rim around a one-step-lighter hole; frames an input
     .track   recessed, holding segments (.seg); the pressed .seg is the raised one
     .action  the one saturated action: accent fill, white --fw-num, 1px top highlight
     .menu    a popover menu; .item its rows

   No outer shadow, no glow; focus is a solid ring. Values come from the SOFT
   section of tokens.css. The surface sets font-family: var(--font-soft) on its
   root; the atoms repeat it so a portaled piece (menu, dialog) is right alone. */

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 999px;
  background: var(--soft-raised);
  box-shadow: var(--soft-hl-soft);
  color: var(--soft-text);
  font-family: var(--font-soft);
  font-size: 14px;
  font-weight: var(--fw-label);
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s var(--soft-ease), transform 0.15s var(--soft-ease);
}

.btn svg {
  flex-shrink: 0;
  color: var(--soft-text-2);
}

.btn:hover:not(:disabled),
.btn[aria-expanded="true"] {
  background: var(--soft-raised-hover);
}

.btn:active:not(:disabled) {
  background: var(--soft-raised-press);
  transform: translateY(1px);
}

/* a control that is not there yet ("coming soon"): flat, faint, inert */
.btn:disabled {
  background: var(--soft-fill);
  box-shadow: none;
  color: var(--soft-faint);
  cursor: default;
}

.btn:disabled svg {
  color: var(--soft-faint);
}

.round {
  width: 36px;
  padding: 0;
}

.round.small {
  width: 32px;
  height: 32px;
}

/* recessed: a 3px tonal rim around a hole one step lighter than the block */
.field,
.track {
  display: flex;
  align-items: center;
  height: 36px;
  border: 3px solid var(--soft-rim);
  border-radius: 999px;
  background: var(--soft-fill);
  font-family: var(--font-soft);
  transition: border-color 0.15s var(--soft-ease);
}

.field {
  gap: 9px;
  padding: 0 5px 0 11px;
  cursor: text;
}

.field svg {
  flex-shrink: 0;
  color: var(--soft-muted);
}

.field input {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  outline: none;
  background: none;
  color: var(--soft-text);
  font: inherit;
  font-weight: var(--fw-label);
}

.field input::placeholder {
  color: var(--soft-faint);
  opacity: 1;
}

.field:hover {
  border-color: var(--soft-raised-hover);
}

.field:focus-within {
  border-color: #474747;
}

.track {
  gap: 2px;
  min-width: 0;
  margin: 0;
  padding: 0 2px;
}

.seg {
  height: 26px;
  padding: 0 11px;
  border: 0;
  border-radius: 999px;
  background: none;
  color: var(--soft-muted);
  font-family: var(--font-soft);
  font-size: 14px;
  font-weight: var(--fw-label);
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s var(--soft-ease), color 0.15s var(--soft-ease), transform 0.15s
    var(--soft-ease);
}

.seg:hover {
  background: var(--soft-seg-hover);
  color: var(--soft-text-2);
}

.seg:active {
  transform: translateY(1px);
}

.seg[aria-pressed="true"],
.seg[aria-checked="true"],
.seg[aria-selected="true"] {
  background: var(--soft-seg-on);
  box-shadow: var(--soft-hl-soft);
  color: #ffffff;
}

/* action: one flat saturated colour + a 1px top highlight */
.action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 20px;
  border: 0;
  border-radius: 999px;
  background: var(--accent);
  box-shadow: var(--soft-hl);
  color: #ffffff;
  font-family: var(--font-soft);
  font-size: 14px;
  font-weight: var(--fw-num);
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition: filter 0.15s var(--soft-ease), transform 0.15s var(--soft-ease);
}

.action:hover {
  filter: brightness(1.08);
}

.action:active {
  filter: brightness(0.92);
  transform: translateY(1px);
}

.action:disabled {
  filter: grayscale(0.4) brightness(0.7);
  cursor: default;
}

/* a popover menu: a lighter ground, one 1px edge, rows inset 4 */
.menu {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  border: 1px solid var(--soft-menu-line);
  border-radius: 14px;
  background: var(--soft-menu);
  font-family: var(--font-soft);
}

.item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 0;
  border-radius: 10px;
  background: none;
  color: var(--soft-text);
  font-family: var(--font-soft);
  font-size: 13px;
  font-weight: var(--fw-label);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s var(--soft-ease);
}

.item svg {
  flex-shrink: 0;
  color: var(--soft-text-2);
}

.item:hover:not(:disabled) {
  background: var(--soft-menu-hover);
}

.item:disabled {
  color: var(--soft-muted);
  cursor: default;
}

.btn:focus-visible,
.action:focus-visible,
.item:focus-visible,
.field:focus-visible {
  outline: 2px solid var(--soft-focus);
  outline-offset: 2px;
}

.seg:focus-visible {
  outline: 2px solid var(--soft-focus);
  outline-offset: 0;
}

/* Words beside a mark (2026-11-03, the operator: "so new work is aligned
   from the start and I write about design, not offsets"). The words' box is
   cut to their capitals and digits (Nunito: 0.72em), so centring them against
   a mark puts the mark on the middle of the digits — by construction, at
   every size and every zoom. Wrap the words of any row that holds a mark (an
   icon, the gem, a glyph) in this; never move a mark by pixels. A box that
   should stay as tall as its line (a table cell, a paragraph) is not this. */
/* (its dashes: the case feature raises "—" and "–" onto the capitals' middle,
   "Wils Case Dash" in tokens.css) */
.fig {
  /* biome-ignore lint/correctness/noUnknownProperty: CSS Text 4 text-box, in Chrome 133+ and Safari 18.2+ */
  text-box: trim-both cap alphabetic;
  font-feature-settings: "case"; /* its dashes on the capitals' middle (Wils Case Dash, tokens.css) */
}

/* A form field's figures beside a mark (2026-11-03). text-box cannot cut the
   text of an <input>, so a field is set in "Nunito Field": Nunito's own files
   (the same @fontsource-variable/nunito woff2, one download) with its ascent
   set to the capital height and no descent, so that its line box is its
   capitals, the cut that text-box makes, drawn by the font itself. From the
   font's tables (hhea = OS/2 typo, every subset, per 1000): ascender 1011,
   descender 353, line gap 0, capHeight 705; no MVAR, so every weight is the
   same. A field with figField takes the line's normal height (the capitals)
   and a text field centres its line in its own box: give the field the
   height of its row and its capitals sit on the row's middle, at any size.
   An ascent and descent that only re-balance (A - D = cap, A + D kept) do
   not do this: Chrome rounds the ascent, the descent and the half-leading to
   whole pixels, and at the amount's 13/16 and 14/18 the baseline landed on
   the same pixel as with the font's own metrics (measured, 2026-11-03). Put
   figField on the field; never move its text by pixels. */
@font-face {
  font-family: "Nunito Field";
  font-style: normal;
  font-display: swap;
  font-weight: 200 1000;
  src: url("@fontsource-variable/nunito/files/nunito-latin-wght-normal.woff2")
    format("woff2-variations");
  unicode-range:
    U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304,
    U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  ascent-override: 70.5%;
  descent-override: 0%;
  line-gap-override: 0%;
}

@font-face {
  font-family: "Nunito Field";
  font-style: normal;
  font-display: swap;
  font-weight: 200 1000;
  src: url("@fontsource-variable/nunito/files/nunito-latin-ext-wght-normal.woff2")
    format("woff2-variations");
  unicode-range:
    U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308,
    U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113,
    U+2C60-2C7F, U+A720-A7FF;
  ascent-override: 70.5%;
  descent-override: 0%;
  line-gap-override: 0%;
}

@font-face {
  font-family: "Nunito Field";
  font-style: normal;
  font-display: swap;
  font-weight: 200 1000;
  src: url("@fontsource-variable/nunito/files/nunito-cyrillic-ext-wght-normal.woff2")
    format("woff2-variations");
  unicode-range: U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F;
  ascent-override: 70.5%;
  descent-override: 0%;
  line-gap-override: 0%;
}

@font-face {
  font-family: "Nunito Field";
  font-style: normal;
  font-display: swap;
  font-weight: 200 1000;
  src: url("@fontsource-variable/nunito/files/nunito-cyrillic-wght-normal.woff2")
    format("woff2-variations");
  unicode-range: U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116;
  ascent-override: 70.5%;
  descent-override: 0%;
  line-gap-override: 0%;
}

@font-face {
  font-family: "Nunito Field";
  font-style: normal;
  font-display: swap;
  font-weight: 200 1000;
  src: url("@fontsource-variable/nunito/files/nunito-vietnamese-wght-normal.woff2")
    format("woff2-variations");
  unicode-range:
    U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0,
    U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB;
  ascent-override: 70.5%;
  descent-override: 0%;
  line-gap-override: 0%;
}

.figField {
  font-family: "Nunito Field", var(--font-soft);
  line-height: normal;
}
